/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, memo } from 'react';
import QSModal from '../modal';
import QSTypography from '../../atoms/typography';
import QSIconButton from '../../atoms/icon-button';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as ExternalLinkIcon } from '../../../assets/svg/external_link.svg';
import ErrorTriangleIcon from '../../../icons/icons-revamp/error_triangle';
import { META_CALL_RESTRICTIONS_GUIDELINES_LINK } from '../../../../modules/home/v1/constants';
import { getChannelNameText } from '../../atoms/call-restriction-notification/helpers';

interface Props {
  open: boolean;
  onClose: () => void;
  channelName?: string[];
  metaGuidelinesUrl?: string;
}

const CallRestrictionInfoModal: FC<Props> = memo(
  ({ open, onClose, channelName = ['your WABA'], metaGuidelinesUrl }) => {
    const channelNameText = getChannelNameText(channelName);

    return (
      <QSModal open={open} onClose={onClose}>
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: #ffffff;
            border-radius: 8px;
            width: 560px;
            max-width: calc(100vw - 32px);
            max-height: calc(100vh - 32px);
            overflow-y: auto;
            outline: none;
          `}
        >
          <QSIconButton
            onClick={onClose}
            css={css`
              position: absolute;
              top: 16px;
              right: 16px;
              color: #8484a8;
              box-shadow: none;
            `}
          >
            <CloseIcon
              css={css`
                font-size: 20px;
              `}
            />
          </QSIconButton>

          {/* Body */}
          <div
            css={css`
              padding: 24px 24px 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
            `}
          >
            {/* Icon */}
            <div
              css={css`
                width: 48px;
                height: 48px;
                border-radius: 50%;
                background-color: #fff3d6;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;
              `}
            >
              <ErrorTriangleIcon size={24} />
            </div>

            {/* Title */}
            <QSTypography
              css={css`
                font-size: 16px;
                font-weight: 700;
                color: #2d2d2d;
                line-height: 1.3;
                margin-bottom: 8px;
              `}
            >
              Your WhatsApp calling may be restricted soon
            </QSTypography>

            {/* Subtitle */}
            <QSTypography
              css={css`
                font-size: 14px;
                color: #7a7a7a;
                margin-bottom: 16px;
              `}
            >
              Several users have blocked or reported your calls. Which may be due to low call pickup rates. Meta may
              temporarily restrict calling on{' '}
              <strong
                css={css`
                  color: #2d2d2d;
                `}
              >
                {channelNameText}
              </strong>{' '}
              for 7 days.
            </QSTypography>

            {/* What can you do card */}
            <div
              css={css`
                width: 100%;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 16px;
                text-align: left;
                margin-bottom: 8px;
              `}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 600;
                  color: #e67e00;
                  margin-bottom: 12px;
                `}
              >
                You will not be able to -
              </QSTypography>

              <ol
                css={css`
                  margin: 0;
                  padding-left: 20px;
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                  li::marker {
                    color: #7a7a7a;
                    font-size: 12px;
                    font-weight: 400;
                  }
                `}
              >
                <li>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #7a7a7a;
                    `}
                  >
                    Receive calls from your customers.
                  </QSTypography>
                </li>
                <li>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #7a7a7a;
                    `}
                  >
                    Have call icon visible to your customers.
                  </QSTypography>
                </li>
              </ol>

              <div
                css={css`
                  border-top: 1px solid #f0f0f0;
                  margin: 16px -16px;
                `}
              />

              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 600;
                  color: #3d906d;
                  margin-bottom: 12px;
                  margin-top: 16px;
                `}
              >
                What can you do?
              </QSTypography>

              <ol
                css={css`
                  margin: 0;
                  padding-left: 20px;
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                  li::marker {
                    color: #7a7a7a;
                    font-size: 12px;
                    font-weight: 400;
                  }
                `}
              >
                <li>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #7a7a7a;
                    `}
                  >
                    Identify and address the cause of calls not being picked up and make sure you are properly resourced
                    to handle expected call volumes.
                  </QSTypography>
                </li>
                <li>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #7a7a7a;
                    `}
                  >
                    Hide call buttons for Users. Go to Settings &gt; Manage Channels &gt; Update channel profile &gt;
                    toggle off Display call buttons.
                  </QSTypography>
                </li>
                <li>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      color: #7a7a7a;
                    `}
                  >
                    If issues persist, consider turning off calling until engagement improves.
                  </QSTypography>
                </li>
              </ol>
            </div>
          </div>

          {/* Footer */}
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              padding: 16px 40px;
              border-top: 1px solid #f0f0f0;
              margin-top: 16px;
            `}
          >
            <a
              href={metaGuidelinesUrl ?? META_CALL_RESTRICTIONS_GUIDELINES_LINK}
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font-size: 14px;
                font-weight: 500;
                color: #7a7a7a;
                text-decoration: none;
                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              Read Meta's guidelines
              <ExternalLinkIcon width={16} height={16} />
            </a>
          </div>
        </div>
      </QSModal>
    );
  }
);

export default CallRestrictionInfoModal;
